import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500&display=swap');

  .login-bg {
    min-height: 100vh;
    background: #1a0a0a;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    position: relative;
    overflow: hidden;
  }

  .login-bg::before {
    content: '';
    position: absolute;
    top: -200px;
    left: -200px;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(185,28,28,0.15) 0%, transparent 70%);
    pointer-events: none;
  }

  .login-bg::after {
    content: '';
    position: absolute;
    bottom: -200px;
    right: -200px;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(185,28,28,0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  .login-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 44px 40px;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 25px 60px rgba(0,0,0,0.4);
    position: relative;
    z-index: 1;
    animation: fadeUp .35s ease;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: none; }
  }

  .login-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
  }

  .login-logo-icon {
    width: 44px;
    height: 44px;
    background: #b91c1c;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 16px;
    color: white;
    letter-spacing: -.5px;
  }

  .login-logo-text {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: #1c1917;
    line-height: 1.1;
  }

  .login-logo-sub {
    font-size: 11px;
    color: #a8a29e;
    letter-spacing: .08em;
    text-transform: uppercase;
  }

  .login-title {
    font-family: 'Syne', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: #1c1917;
    margin-bottom: 4px;
  }

  .login-subtitle {
    font-size: 13px;
    color: #a8a29e;
    margin-bottom: 28px;
  }

  .login-form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 16px;
  }

  .login-label {
    font-size: 12px;
    font-weight: 500;
    color: #57534e;
  }

  .login-input {
    padding: 10px 12px;
    border: 1.5px solid #e5e3df;
    border-radius: 8px;
    font-size: 14px;
    color: #1c1917;
    outline: none;
    font-family: 'DM Sans', sans-serif;
    transition: border .15s, box-shadow .15s;
    background: #fafaf9;
  }

  .login-input:focus {
    border-color: #b91c1c;
    box-shadow: 0 0 0 3px rgba(185,28,28,.1);
    background: #fff;
  }

  .login-input.error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239,68,68,.1);
  }

  .login-error {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    color: #b91c1c;
    margin-bottom: 16px;
    animation: shake .3s ease;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25%       { transform: translateX(-6px); }
    75%       { transform: translateX(6px); }
  }

  .login-btn {
    width: 100%;
    padding: 11px;
    background: #b91c1c;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: background .15s, transform .1s;
    margin-top: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .login-btn:hover:not(:disabled) { background: #991b1b; }
  .login-btn:active:not(:disabled) { transform: scale(.98); }
  .login-btn:disabled { opacity: .6; cursor: not-allowed; }

  .login-footer {
    margin-top: 24px;
    text-align: center;
    font-size: 12px;
    color: #a8a29e;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin .7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .lang-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
  }

  .lang-toggle-login {
    display: flex;
    background: #f3f4f6;
    border-radius: 6px;
    overflow: hidden;
  }

  .lang-btn-login {
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    background: transparent;
    color: #9ca3af;
    transition: all .15s;
    font-family: 'DM Sans', sans-serif;
    letter-spacing: .04em;
  }

  .lang-btn-login.active {
    background: #b91c1c;
    color: white;
    border-radius: 6px;
  }
`;

const TEXT = {
  fr: {
    title: "Bon retour!",
    subtitle: "Connectez-vous à votre compte TAF CRM",
    email: "Adresse courriel",
    password: "Mot de passe",
    emailPlaceholder: "info@tafexcavation.com",
    passwordPlaceholder: "••••••••",
    btn: "Se connecter",
    loading: "Connexion...",
    footer: "Taylor Amyotte et Fils — Système CRM",
    errors: {
      "auth/invalid-email": "Adresse courriel invalide.",
      "auth/user-not-found": "Aucun compte trouvé avec ce courriel.",
      "auth/wrong-password": "Mot de passe incorrect.",
      "auth/too-many-requests": "Trop de tentatives. Veuillez réessayer plus tard.",
      "auth/invalid-credential": "Courriel ou mot de passe incorrect.",
      default: "Une erreur est survenue. Veuillez réessayer."
    }
  },
  en: {
    title: "Welcome back!",
    subtitle: "Sign in to your TAF CRM account",
    email: "Email address",
    password: "Password",
    emailPlaceholder: "info@tafexcavation.com",
    passwordPlaceholder: "••••••••",
    btn: "Sign in",
    loading: "Signing in...",
    footer: "Taylor Amyotte et Fils — CRM System",
    errors: {
      "auth/invalid-email": "Invalid email address.",
      "auth/user-not-found": "No account found with this email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/too-many-requests": "Too many attempts. Please try again later.",
      "auth/invalid-credential": "Incorrect email or password.",
      default: "An error occurred. Please try again."
    }
  }
};

export default function Login({ onLogin }) {
  const [lang, setLang] = useState("fr");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const t = TEXT[lang];

  const handleSubmit = async () => {
    if (!email || !password) {
      setError(t.errors.default);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      onLogin(result.user);
    } catch (err) {
      setError(t.errors[err.code] || t.errors.default);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="login-bg">
        <div className="login-card">

          {/* Language Toggle */}
          <div className="lang-row">
            <div className="lang-toggle-login">
              <button className={`lang-btn-login${lang==="fr"?" active":""}`} onClick={()=>setLang("fr")}>FR</button>
              <button className={`lang-btn-login${lang==="en"?" active":""}`} onClick={()=>setLang("en")}>EN</button>
            </div>
          </div>

          {/* Logo */}
          <div className="login-logo">
            <div className="login-logo-icon">TAF</div>
            <div>
              <div className="login-logo-text">TAF CRM</div>
              <div className="login-logo-sub">BY MCA</div>
            </div>
          </div>

          {/* Heading */}
          <div className="login-title">{t.title}</div>
          <div className="login-subtitle">{t.subtitle}</div>

          {/* Error */}
          {error && (
            <div className="login-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {/* Form */}
          <div className="login-form-group">
            <label className="login-label">{t.email}</label>
            <input
              className={`login-input${error?" error":""}`}
              type="email"
              placeholder={t.emailPlaceholder}
              value={email}
              onChange={e => { setEmail(e.target.value); setError(""); }}
              onKeyDown={handleKeyDown}
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="login-form-group">
            <label className="login-label">{t.password}</label>
            <input
              className={`login-input${error?" error":""}`}
              type="password"
              placeholder={t.passwordPlaceholder}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(""); }}
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
            />
          </div>

          {/* Button */}
          <button className="login-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <><div className="spinner"/>{t.loading}</>
            ) : t.btn}
          </button>

          <div className="login-footer">{t.footer}</div>
        </div>
      </div>
    </>
  );
}